import clsx from 'clsx';
import { useRef } from 'react';
import { FOLDER_FILL_COLOR, FOLDER_STROKE_COLOR, ITEM_ACTIVE_BG_COLOR, ITEM_HOVER_BG_COLOR } from '../../../constants';
import { useDetectTextOverflow } from '../../../hooks';
import { ComponentSize, DropdownPlacement, FolderHierarchy } from '../../../types';
import { ButtonColor, ButtonVariant, IconButton } from '../Button';
import { Icon, Icons } from '../Icons';
import { Typography, TypographyType } from '../Typography';

const MAX_LINES = 1;
const ITEM_X_PADDING = 4;
const ITEM_GAP = 8;
const ITEM_ICON_SIZE = 24;

interface FolderTreeItemProps {
  folders: FolderHierarchy[];
  id: string;
  level: number;
  name: string;
  openFolderIds: string[];
  selected: string | null;
  toggleOpenFolder: (id: string) => void;
  toggleSelectFolder: (id: string) => void;
  openFolderPathIds?: string[];
}

const FolderTreeItem = ({
  folders,
  id,
  level,
  name,
  openFolderIds,
  selected,
  toggleOpenFolder,
  toggleSelectFolder,
  openFolderPathIds = [],
}: FolderTreeItemProps) => {
  const nameRef = useRef<HTMLDivElement>(null);
  const isNameOverflowing = useDetectTextOverflow(nameRef, MAX_LINES);
  const nameTooltip = isNameOverflowing ? { content: name, placement: DropdownPlacement.BOTTOM } : undefined;

  const isOpen = openFolderIds.includes(id);
  const isSelected = selected === id;

  return (
    <div className="flex flex-col gap-2">
      <div
        className={clsx(
          'flex h-8 w-fit min-w-full cursor-pointer items-center rounded-[4px]',
          isSelected ? ITEM_ACTIVE_BG_COLOR : ITEM_HOVER_BG_COLOR
        )}
        style={{
          gap: ITEM_GAP,
          paddingLeft: ITEM_X_PADDING + level * (ITEM_ICON_SIZE * 2 + ITEM_GAP * 2),
          paddingRight: ITEM_X_PADDING,
        }}
        onClick={() => toggleSelectFolder(id)}
        onDoubleClick={() => {
          toggleOpenFolder(id);
          if (selected !== id) {
            toggleSelectFolder(id);
          }
        }}
      >
        {/* Show chevron icon for folders with children, otherwise show empty spacer */}
        {!!folders.length && (
          <IconButton
            active={isOpen}
            color={ButtonColor.NEUTRAL}
            icon={isOpen ? Icon.CHEVRON_RIGHT : Icon.CHEVRON_DOWN}
            size={ComponentSize.X_SMALL}
            onClick={() => toggleOpenFolder(id)}
            variant={ButtonVariant.GHOST}
          />
        )}
        {!folders.length && <div className="h-6 w-6" />}
        <Icons
          icon={isOpen ? Icon.FOLDER_OPEN : Icon.FOLDER}
          size={ComponentSize.LARGE}
          fillColor={FOLDER_FILL_COLOR}
          color={FOLDER_STROKE_COLOR}
        />
        <Typography className="select-none" ref={nameRef} type={TypographyType.BASE} tooltip={nameTooltip} noWrap>
          {name}
        </Typography>
      </div>
      {isOpen && !!folders.length && (
        <div className="flex w-full flex-col gap-2">
          {folders.map((folder) => (
            <FolderTreeItem
              key={folder.id}
              folders={folder.children}
              id={folder.id}
              level={level + 1}
              name={folder.name}
              openFolderIds={openFolderIds}
              selected={selected}
              toggleSelectFolder={toggleSelectFolder}
              toggleOpenFolder={toggleOpenFolder}
              openFolderPathIds={openFolderPathIds.slice(1)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default FolderTreeItem;
